<script setup lang="ts">
/**
 * Компонент для отображения информации о продукте (описание, вес, КБЖУ)
 * Следует Vue Style Guide: Priority A
 */

import { computed } from 'vue';
import type { ProductKBJU } from '../model/types/product.types';

interface Props {
  description?: string;
  amount?: number;
  volume?: number;
  weight?: number;
  kbju?: ProductKBJU;
  /** Состав блюда — обязателен для продуктов питания (п. 2 ст. 10 ЗоЗПП). */
  composition?: string | null;
  /** Аллергены — выводятся отдельным заметным блоком. */
  allergens?: string | null;
  tag?: string;
  showDescription?: boolean;
  showMeasurements?: boolean;
  showKbju?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showDescription: true,
  showMeasurements: true,
  showKbju: true,
  composition: null,
  allergens: null,
});

// Есть ли КБЖУ данные
const hasKbju = computed(() => {
  if (!props.kbju) return false;
  return !!(props.kbju.proteins || props.kbju.fats || props.kbju.carbohydrates || props.kbju.calories);
});

// Есть ли измерения
const hasMeasurements = computed(() => {
  return !!(props.amount || props.volume || props.weight);
});
</script>

<template>
  <div class="product-info">
    <!-- Badge -->
    <ProductBadge v-if="tag" :tag="tag" size="md" class-name="mt-3" />

    <!-- Описание -->
    <div v-if="showDescription && description" class="product-info__description">
      {{ description }}
    </div>

    <!-- Измерения (вес, объем, количество) -->
    <div v-if="showMeasurements && hasMeasurements" class="product-info__measurements">
      <span v-if="amount">{{ amount }} шт.</span>
      <span v-if="volume">{{ volume }} мл.</span>
      <span v-if="weight">{{ weight }} г.</span>
    </div>

    <!-- КБЖУ -->
    <div v-if="showKbju && hasKbju" class="product-info__kbju">
      <div v-if="kbju?.proteins" class="product-info__kbju-item">
        <div class="product-info__kbju-value">{{ kbju.proteins }}</div>
        <div class="product-info__kbju-label">Белки</div>
      </div>
      <div v-if="kbju?.fats" class="product-info__kbju-item">
        <div class="product-info__kbju-value">{{ kbju.fats }}</div>
        <div class="product-info__kbju-label">Жиры</div>
      </div>
      <div v-if="kbju?.carbohydrates" class="product-info__kbju-item">
        <div class="product-info__kbju-value">{{ kbju.carbohydrates }}</div>
        <div class="product-info__kbju-label">Углеводы</div>
      </div>
      <div v-if="kbju?.calories" class="product-info__kbju-item">
        <div class="product-info__kbju-value">{{ kbju.calories }}</div>
        <div class="product-info__kbju-label">Калории</div>
      </div>
    </div>

    <!-- Состав и аллергены. П. 2 ст. 10 ЗоЗПП: для продуктов питания состав
         обязателен, и при дистанционной продаже покупатель должен видеть его
         до оформления заказа — то есть здесь, в карточке. -->
    <div v-if="composition" class="product-info__composition">
      <div class="product-info__composition-label">Состав</div>
      <p class="product-info__composition-text">{{ composition }}</p>
    </div>

    <div v-if="allergens" class="product-info__allergens">
      <div class="product-info__allergens-label">Аллергены</div>
      <p class="product-info__allergens-text">{{ allergens }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.product-info {
  &__description {
    @apply my-3 text-xs;
  }

  &__measurements {
    @apply flex gap-3 text-xs mb-4;
  }

  &__kbju {
    @apply flex justify-between gap-4 p-3 bg-neutral-800 text-white rounded-xl my-8;
  }

  &__kbju-item {
    @apply flex flex-col text-center text-base;
  }

  &__kbju-value {
    @apply font-medium;
  }

  &__kbju-label {
    @apply text-xs;
  }

  &__composition {
    @apply mb-3;
  }

  &__composition-label,
  &__allergens-label {
    @apply text-xs font-semibold uppercase tracking-wide opacity-60 mb-1;
  }

  &__composition-text,
  &__allergens-text {
    @apply text-xs leading-relaxed;
  }

  // Аллергены выделяем: по ним принимают решение о покупке, а не просто
  // знакомятся с составом.
  &__allergens {
    @apply mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900;
  }
}
</style>
